import { Badge, cn } from '@kalos/ui';
import { ChevronRight } from 'lucide-react';
import { type ReactNode } from 'react';

/**
 * The list that holds `ListRow`s. Each row draws its own rule, inset to where its text starts;
 * no card, border or radius.
 */
export const ListRows = ({
  label,
  children,
  className,
}: {
  /** Names the list for screen readers, for example "Documents". */
  label: string;
  children: ReactNode;
  className?: string;
}) => (
  <ul aria-label={label} className={className}>
    {children}
  </ul>
);

type ListRowProps = {
  /**
   * The record's name. Wraps to two lines, then ends in an ellipsis: rows are 56px with a
   * one-line name and 72px with two. The full name stays in the row's accessible name.
   */
  title: string;
  /** A one-word flag after the name, such as "Prime". Read with the name by screen readers. */
  badge?: string;
  /** Optional muted line under the name, truncated if it's long. Leave it out unless it helps people tell records apart. */
  details?: ReactNode;
  /** Let the details wrap to a second line before truncating, when they're what tells similar rows apart. */
  detailsTwoLines?: boolean;
  /** A small icon before the name that signals what tapping does. Decorative to screen readers. */
  leading?: ReactNode;
  /** What tapping the row does, read before the title by screen readers, for example "Open link". */
  actionLabel: string;
  onSelect: () => void;
  disabled?: boolean;
  /** One control at the right edge, such as a "More actions" menu. Sits outside the row's tap target. */
  trailing?: ReactNode;
  /** A chevron at the right edge, inside the tap target, for a row that opens a page. */
  showChevron?: boolean;
  /** A short value at the right inside the tap target, such as an amount, set in bold. */
  value?: ReactNode;
  /** A small icon at the right edge inside the tap target, such as a chevron. Decorative. */
  trailingIcon?: ReactNode;
};

/**
 * A row in a phone list of records with no imagery and one primary action: an optional type icon
 * and the record's name, 56 to 72px tall. The whole row is the tap target for that action.
 */
export const ListRow = ({
  title,
  badge,
  details,
  detailsTwoLines = false,
  leading,
  actionLabel,
  onSelect,
  disabled,
  trailing,
  showChevron = false,
  value,
  trailingIcon,
}: ListRowProps) => (
  <li
    className={cn(
      'after:bg-border relative flex items-center after:absolute after:bottom-0 after:right-0 after:h-px last:after:hidden',
      // The rule starts where the text does: past the row padding, the icon and the gap after it.
      leading ? 'after:left-11' : 'after:left-4',
    )}
  >
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-label={`${actionLabel}: ${title}${badge ? `, ${badge}` : ''}`}
      className="active:bg-muted flex min-h-14 min-w-0 flex-1 items-center gap-3 px-4 py-4 text-left disabled:opacity-50"
    >
      {leading && (
        <span aria-hidden className="text-muted-foreground flex h-5 shrink-0 items-center">
          {leading}
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-foreground line-clamp-2 break-words text-sm font-semibold">
          {title}
          {badge && (
            <Badge variant="secondary" className="ml-2 align-middle font-medium">
              {badge}
            </Badge>
          )}
        </span>
        {details && (
          <span
            className={cn(
              'text-muted-foreground text-sm',
              detailsTwoLines ? 'line-clamp-2 break-words' : 'truncate',
            )}
          >
            {details}
          </span>
        )}
      </span>
      {value != null && (
        <span className="text-foreground shrink-0 self-start pt-0.5 text-sm font-semibold tabular-nums">
          {value}
        </span>
      )}
      {showChevron && (
        <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
      )}
      {trailingIcon && (
        <span aria-hidden className="text-muted-foreground flex shrink-0 items-center">
          {trailingIcon}
        </span>
      )}
    </button>
    {trailing && <div className="shrink-0 pr-2">{trailing}</div>}
  </li>
);
